import { Component, Input, OnChanges, OnDestroy, OnInit, SimpleChanges, inject } from '@angular/core'
import { FormBuilder, FormControl, FormGroup } from '@angular/forms'
import { ConstructionKeyType } from '@overtur-monorepo/keying-shared'
import { Subject } from 'rxjs'
import { mergeMap, take, takeUntil } from 'rxjs/operators'
import { KeySystemStatuses } from '../../../constants/keySystem-statuses'
import { KeySystem, Structure, StructureDetails } from '../../../models/dev.test.models'
import { ErrorService } from '../../../services/error.service'
import { CylinderDataSets, KeyingCylinderService } from '../../../services/keying.cylinder.service'
import { KeyingDataSets, KeyingDataSetsService, KeySystemDataSets } from '../../../services/keying.data.sets.service'
import { KeyingSystemService } from '../../../services/keying.system.service'
import { KeySystemService } from '../../../services/key-system/key-system.service'

@Component({
  selector: 'app-key-system-card',
  templateUrl: './key-system-card.component.html',
  styleUrls: ['./key-system-card.component.scss'],
  standalone: false,
})
export class KeySystemCardComponent implements OnInit, OnChanges, OnDestroy {
  destroy$ = new Subject()
  keyingDataSets: KeyingDataSets
  cylinderDataSets: CylinderDataSets
  constructionKeyTypeEnum = ConstructionKeyType

  @Input() structure: Structure
  @Input() keySystem: KeySystem
  @Input() keySystemDataSets: KeySystemDataSets
  @Input() keywayFamily
  @Input() combinatingRule

  keySystemStatus = ''
  isReadOnly = true
  editing = false
  expanded = true
  pinningsGenerated = false
  disableConstruction: boolean
  hasSlOrder: boolean

  editKeySystemForm: FormGroup
  availableConstructionKeyTypes = []
  selectedChamberType
  selectedConstructionKeyType
  originalOrder
  constructionKeyType
  keySystemOrderNumber

  focus = {
    constructionKeyType: false,
  }

  public errorMessages = {
    constructionKeyType: [{ type: 'required', message: 'A Construction Key Type is required.' }],
  }

  private keyingSystemService = inject(KeyingSystemService)
  private keyingDataSetsService = inject(KeyingDataSetsService)
  private errorService = inject(ErrorService)
  public form = inject(FormBuilder)
  private keyingCylinderService = inject(KeyingCylinderService)
  private keySystemService = inject(KeySystemService)

  /* istanbul ignore next */
  ngOnInit(): void {
    this.keyingDataSetsService.keyingDataSets$
      .pipe(takeUntil(this.destroy$))
      .subscribe((keyingDataSets: KeyingDataSets) => {
        if (!keyingDataSets) {
          return
        }

        setTimeout(() => {
          this.keyingDataSets = keyingDataSets
          this.constructionKeyType = this.keyingDataSets.constructionTypes.find(
            (ct) => ct.id === this.keySystem?.constructionTypeId
          )

          this.editKeySystemForm = this.form.group({
            constructionKeyType: new FormControl(''),
            orderId: new FormControl(''),
          })

          this.getConstructionTypesFromAttributes()
          this.getOrderNumberFromAttributes()

          if (
            this.keySystem?.constructionTypeId &&
            this.keySystem.constructionTypeId !== this.constructionKeyTypeEnum.None
          ) {
            this.loadPinningsGeneratedStatus()
          }

          this.notifyDuplicateSymbols()
          this.loadHasSlOrder()
        }, 0)
      })

    this.keyingDataSetsService.structureDetails$
      .pipe(takeUntil(this.destroy$))
      .subscribe((details: StructureDetails) => {
        this.isReadOnly =
          details?.mecsInProgress || details?.publishingInProgress || details?.pinningGenerationInprogress
      })
  }

  /* istanbul ignore next */
  ngOnChanges(changes: SimpleChanges): void {
    if (changes.keySystem && this.keyingDataSets) {
      const statusId = changes.keySystem.currentValue.statusId
      if (statusId) {
        Object.keys(KeySystemStatuses).forEach((x) => {
          if (x == statusId.toString()) {
            this.keySystemStatus = KeySystemStatuses[x]
          }
        })
      }
      this.constructionKeyType = this.keyingDataSets.constructionTypes.find(
        (ct) => ct.id === changes.keySystem.currentValue.constructionTypeId
      )
      this.getConstructionTypesFromAttributes()
      this.getOrderNumberFromAttributes()
    }
  }

  constructionKeyDisabled(): boolean {
    return this.isDealerKeySystem() || this.hasConstructionType()
  }

  private isDealerKeySystem(): boolean {
    return this.keySystem.isDealerKeySystem
  }

  private hasConstructionType(): boolean {
    if (!this.keySystem.constructionTypeId) return false
    if (this.keySystem.constructionTypeId === this.constructionKeyTypeEnum.None) return false
    return this.pinningsGenerated
  }

  private loadPinningsGeneratedStatus(): void {
    this.keySystemService
      .getCylindersCountWithPinnings(this.structure.id, this.keySystem.id)
      .pipe(takeUntil(this.destroy$))
      .subscribe((cylindersCount) => {
        this.pinningsGenerated = cylindersCount > 0
      })
  }

  private loadHasSlOrder(): void {
    this.keySystemService
      .hasSecureLPinOrder(this.structure.id, this.keySystem.id)
      .pipe(takeUntil(this.destroy$))
      .subscribe((hasSlOrder) => {
        this.hasSlOrder = hasSlOrder
      })
  }

  private notifyDuplicateSymbols(): void {
    this.keySystemService
      .getDuplicateSymbols(this.structure.id, this.keySystem.id)
      .pipe(takeUntil(this.destroy$))
      .subscribe((duplicateSymbols) => {
        const keySymbols = duplicateSymbols.keySymbols
        const cylinderSymbols = duplicateSymbols.cylinderSymbols

        if (keySymbols && keySymbols.length > 0) {
          const keySymbolsList = keySymbols.join(', ')
          this.errorService.showError(`Duplicate Key Symbols found: ${keySymbolsList}`)
        }

        if (cylinderSymbols && cylinderSymbols.length > 0) {
          const cylinderSymbolsList = cylinderSymbols.join(', ')
          this.errorService.showError(`Duplicate Cylinder Symbols found: ${cylinderSymbolsList}`)
        }
      })
  }

  /* istanbul ignore next */
  ngOnDestroy() {
    this.destroy$.next(true)
    this.destroy$.complete()
  }

  getConstructionTypesFromAttributes() {
    if (!this.structure.keywayFamilyId || !this.structure.chamberTypeId) return
    this.availableConstructionKeyTypes = []
    this.keyingDataSets.constructionTypes.forEach((e) => {
      const t = e.attributes.filter(
        (a) => a.keywayFamilyId === this.structure.keywayFamilyId && a.chamberTypeId === this.structure.chamberTypeId
      )
      t.forEach((tt) =>
        this.availableConstructionKeyTypes.push(
          this.keyingDataSets.constructionTypes.find((c) => c.id === tt.constructionTypeId)
        )
      )
    })
    if (this.structure.keywayFamilyId === 11 && this.structure.combinatingRuleSetId === 8) {
      this.availableConstructionKeyTypes = this.availableConstructionKeyTypes.filter((x) => x.id !== 6)
    }

    if (!this.constructionKeyType) {
      this.selectedConstructionKeyType = this.availableConstructionKeyTypes[0]
    } else {
      this.selectedConstructionKeyType = this.constructionKeyType
    }

    this.editKeySystemForm.controls.constructionKeyType.setValue(this.selectedConstructionKeyType)
  }

  /* istanbul ignore next */
  getOrderNumberFromAttributes() {
    this.keySystemOrderNumber = this.keySystem.orderNumber
    this.editKeySystemForm.controls.orderId.setValue(this.keySystemOrderNumber)
  }

  /* istanbul ignore next */
  updateOrderNumber() {
    this.keySystemOrderNumber = this.editKeySystemForm.controls.orderId.value
  }

  /* istanbul ignore next */
  saveKeySystem() {
    const constructionTypeRequest = [
      {
        op: 'replace',
        path: '/constructionTypeId',
        value: this.selectedConstructionKeyType.id,
      },
    ]
    const controls = this.editKeySystemForm.controls
    const originalOrderRequest = [
      {
        op: 'replace',
        path: '/OrderNumber',
        value: this.keySystemOrderNumber,
      },
    ]

    this.keyingSystemService
      .patchKeySystem(this.structure.id, this.keySystem.id, constructionTypeRequest)
      .pipe(
        mergeMap(() =>
          this.keyingSystemService.patchOriginalOrder(this.structure.id, this.keySystem.id, originalOrderRequest)
        ),
        take(1)
      )
      .subscribe({
        next: (res) => {
          this.editing = false
          this.keyingDataSetsService.loadKeySystemDataSets(this.structure.id, this.keySystem.id)
          this.errorService.confirm('Updated Key System')
        },
        error: (error) => {
          this.keyingDataSetsService.loadKeySystemDataSets(this.structure.id, this.keySystem.id)
          this.errorService.throw(error)
        },
      })
  }

  /* istanbul ignore next */
  toggleEdit() {
    this.editing = !this.editing
  }

  /* istanbul ignore next */
  focusOn(focus) {
    this.focus[focus] = !this.focus[focus]
  }

  /* istanbul ignore next */
  fieldHasErrorMessages(form, validation, fieldName) {
    const field = form.get(fieldName)
    return Boolean(field.hasError(validation.type) && (field.dirty || field.touched))
  }

  /* istanbul ignore next */
  fieldHasError(form, fieldName) {
    const field = form.get(fieldName)
    return Boolean(field.invalid && (field.dirty || field.touched))
  }
}
